import { z } from "zod";

// Canonical, cloud-agnostic infrastructure cost line items (Compute, Storage,
// Networking, Database, Security, Analytics & Streaming). These are the
// high-level segments stacked in the Summary "Cloud Costs" lens and the Jobs
// "Cloud Costs" group-by chart. The taxonomy mirrors the backend
// `CLOUD_COST_CATEGORIES` returned by `/cost/cloud/{trends,projection}`.
export const CLOUD_COST_CATEGORIES = [
  "Compute",
  "Storage",
  "Networking",
  "Database",
  "Security",
  "Analytics & Streaming",
] as const;

export type CloudCostCategory = (typeof CLOUD_COST_CATEGORIES)[number];

// Selector / group-by label that activates the cloud-cost breakdown.
export const CLOUD_COSTS_LABEL = "Cloud Costs";

// Per-row total key the backend emits — sums every category segment so the
// shared CostChartTooltip can render its "Total Cost" badge. The prefix is
// reserved so it can never collide with a real category name.
export const CLOUD_TOTAL_KEY = "__cloud_cost_total__";

const CLOUD_COST_COLORS: Record<CloudCostCategory, string> = {
  Compute: "#5B8DEF",
  Storage: "#4CAF50",
  Networking: "#F5A623",
  Database: "#9575CD",
  Security: "#EF5350",
  "Analytics & Streaming": "#26A69A",
};

const FALLBACK_COLOR = "#9E9E9E";

export const cloudCostColorFor = (key: string): string => {
  // The future tooltip resolves color by the estimate key (e.g.
  // "Compute_estimate"); strip the suffix so projected series share the base
  // category color instead of falling back to grey.
  const normalizedKey = key.replace(/_estimate$/, "") as CloudCostCategory;
  return CLOUD_COST_COLORS[normalizedKey] ?? FALLBACK_COLOR;
};

// One stacked-bar period of cloud infrastructure cost by category. Besides
// `period`, the backend sends a float per category, `__cloud_cost_total__`,
// and `percentage_<segment>` / `percentage_total` (number | null) — kept via
// the catchall so the chart + tooltip read them directly.
export const CloudCostTrendRowSchema = z
  .object({ period: z.string() })
  .catchall(z.union([z.number(), z.string(), z.null()]));
export type CloudCostChartRow = z.infer<typeof CloudCostTrendRowSchema>;

// One calendar month of actual + projected cloud cost (Future State lens).
// Per-category actual (`<cat>`) and projected (`<cat>_estimate`) values plus
// `percentage_*` arrive as catchall keys.
export const CloudCostProjectionRowSchema = z
  .object({
    period: z.string(),
    total: z.number(),
    total_estimate: z.number(),
    is_estimate: z.boolean(),
  })
  .catchall(z.union([z.number(), z.string(), z.boolean(), z.null()]));
export type CloudCostProjectionRow = z.infer<typeof CloudCostProjectionRowSchema>;

export const CloudCostTrendsResponseSchema = z.array(CloudCostTrendRowSchema);
export const CloudCostProjectionResponseSchema = z.array(CloudCostProjectionRowSchema);

export interface CloudCostTrendsParams {
  start_date: string;
  end_date: string;
  granularity: "day" | "week" | "month";
  workspace_id?: string;
}

export interface CloudCostProjectionParams {
  workspace_id?: string;
}
